import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Typography,
} from '@mui/material';
import React, { FormEventHandler, useState } from 'react';
import { useHistory } from 'react-router';

import { NavLink as RouterLink } from 'react-router-dom';

import { CrisBrandPicker } from 'app/components/cris-brand-picker';
import { ChannelSelector } from 'app/components/form-controls/channel-selector';
import { EntityTextField } from 'app/components/form-controls/entity-text-field';
import { Channel } from 'app/constants/channel';
import { StoreType } from 'app/constants/store-type';
import { Store } from 'app/model/cris/store';
import { storeService } from 'app/services/store.service';
import { EntityOnChange, ModelWithoutId } from 'app/type';

interface StoreFormProps {
    store: ModelWithoutId<Store>;
    onChange: (store: Store) => void;
}

export const StoreForm: React.FunctionComponent<StoreFormProps> = ({ store, onChange }) => {
    const history = useHistory();
    const isNew = store.id === undefined;
    const cancelLink = store.id === undefined ? '' : `/${store.id}`;

    const [entityData, setEntityData] = useState(store);

    const handleSubmit: FormEventHandler = (evt) => {
        evt.preventDefault();
        storeService.save(entityData).subscribe((updatedEntity) => {
            onChange(updatedEntity);
            history.replace(`/cris/stores/${updatedEntity.id}`);
        });
    };

    const handleEntityUpdate: EntityOnChange<Store, unknown> = (evt, name, value) => {
        if ('name' === name) {
            // eslint-disable-next-line no-param-reassign
            value = (value as string).toUpperCase();
        }
        setEntityData({ ...entityData, [name]: value });
    };

    return (
        <form onSubmit={handleSubmit}>
            <Typography variant='h6'>
                {isNew ? 'Create' : 'Edit'} Store{isNew ? '' : `: ${store.id ?? ''}`}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <Box sx={{ flexBasis: '20em' }} className='mb-1 mr-1'>
                    <CrisBrandPicker name='brand' onChange={handleEntityUpdate} value={entityData.brand} />
                </Box>
                <EntityTextField
                    sx={{ flexBasis: '15em' }}
                    label='Name'
                    name='name'
                    className='mb-1 mr-1'
                    value={entityData}
                    onChange={handleEntityUpdate}
                />
                <EntityTextField
                    label='Store Num'
                    name='storeNum'
                    className='mb-1 mr-1'
                    value={entityData}
                    onChange={handleEntityUpdate}
                />

                <FormControl variant='standard' sx={{ flexBasis: '10em' }} className='mb-1 mr-1'>
                    <InputLabel>Type</InputLabel>
                    {/* TODO make a form component to handle enums automatically */}
                    <Select
                        name='type'
                        value={entityData.type ?? ''}
                        onChange={(event) => handleEntityUpdate(event, 'type', event.target.value)}
                        label='Type'
                    >
                        <MenuItem value={StoreType.NONE}>(none)</MenuItem>
                        <MenuItem value={StoreType.DIST}>Distribution</MenuItem>
                        <MenuItem value={StoreType.PAYMENT}>Payment</MenuItem>
                    </Select>
                </FormControl>
                <EntityTextField
                    label='Location ID'
                    name='locationId'
                    className='mb-1 mr-1'
                    value={entityData}
                    onChange={handleEntityUpdate}
                />
                <EntityTextField
                    label='Storeowner ID'
                    name='storeownerId'
                    className='mb-1 mr-1'
                    value={entityData}
                    onChange={handleEntityUpdate}
                />
                <EntityTextField
                    label='MID Given'
                    name='midGiven'
                    className='mb-1 mr-1'
                    sx={{ flexBasis: '15em' }}
                    value={entityData}
                    onChange={handleEntityUpdate}
                />
                <EntityTextField
                    label='Phone'
                    name='phone'
                    className='mb-1 mr-1'
                    value={entityData}
                    onChange={handleEntityUpdate}
                />
                <EntityTextField
                    label='Domain'
                    name='domain'
                    className='mb-1 mr-1'
                    sx={{ flexBasis: '15m' }}
                    value={entityData}
                    onChange={handleEntityUpdate}
                />
                <EntityTextField
                    label='Date Opened'
                    name='dateOpened'
                    className='mb-1 mr-1'
                    value={entityData}
                    onChange={handleEntityUpdate}
                    type='date'
                    InputLabelProps={{ shrink: true }}
                />
                <EntityTextField
                    label='Date Closed'
                    name='dateClosed'
                    className='mb-1 mr-1'
                    value={entityData}
                    onChange={handleEntityUpdate}
                    type='date'
                    InputLabelProps={{ shrink: true }}
                />
                <EntityTextField
                    label='Source'
                    name='source'
                    className='mb-1 mr-1'
                    value={entityData}
                    onChange={handleEntityUpdate}
                />
                <Box sx={{ flexBasis: '10em' }} className='mb-1 mr-1'>
                    <ChannelSelector
                        label='Default Channel'
                        name='defaultChannel'
                        onChange={(event) => handleEntityUpdate(event, 'defaultChannel', event.target.value as Channel)}
                        value={entityData.defaultChannel}
                    />
                </Box>
                <Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={entityData.isDefault || false}
                                onChange={(event) => handleEntityUpdate(event, 'isDefault', event.target.checked)}
                                name='isDefault'
                            />
                        }
                        label='Default Store'
                    />
                </Box>
            </Box>

            <Button type='submit' variant='outlined' color='primary' className='mt-2'>
                Save
            </Button>
            <Button
                type='button'
                variant='outlined'
                color='inherit'
                className='mt-2 ml-1'
                component={RouterLink}
                to={`/cris/stores${cancelLink}`}
            >
                Cancel
            </Button>
        </form>
    );
};
